import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api, setSessionToken } from '../api';
import { toast } from 'sonner';
import { User, Shield, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const AccountPicker = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dependents, setDependents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVerify, setShowVerify] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [selectedDependent, setSelectedDependent] = useState(null);

  useEffect(() => {
    api.get('/guardian/my-dependents-for-picker')
      .then(res => setDependents(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // If no dependents, go straight to dashboard
  useEffect(() => {
    if (!loading && dependents.length === 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, dependents, navigate]);

  const handleSelectSelf = () => {
    navigate('/dashboard', { replace: true });
  };

  const handleSelectDependent = async (dep) => {
    setSelectedDependent(dep);
    try {
      // First check if already verified this session
      const checkRes = await api.get('/guardian/check-verified');
      if (checkRes.data.verified) {
        await doSwitch(dep);
        return;
      }
      // Not verified — prompt for password (no email needed)
      setVerifyPassword('');
      setShowVerify(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start verification');
    }
  };

  const handleVerifyAndSwitch = async () => {
    if (!verifyPassword) return;
    setVerifying(true);
    try {
      await api.post('/guardian/verify-password', { password: verifyPassword });
      toast.success('Verified');
      setShowVerify(false);
      setVerifyPassword('');
      await doSwitch(selectedDependent);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Incorrect password');
    } finally {
      setVerifying(false);
    }
  };

  const doSwitch = async (dep) => {
    try {
      const res = await api.post('/guardian/switch-account', { dependent_user_id: dep.user_id });
      if (res.data.token) {
        setSessionToken(res.data.token);
        // Clear the cookie so it doesn't override the new Bearer token
        document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
      toast.success(res.data.message);
      // Force full page reload to re-authenticate with new token
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to switch account');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-amber-50">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-amber-50 p-4">
      <div className="w-full max-w-md space-y-4">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-heading font-bold text-slate-800">Welcome, {user?.name?.split(' ')[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">Which account would you like to access?</p>
        </div>

        {/* Own account */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-primary/30"
          onClick={handleSelectSelf} data-testid="select-self">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-heading font-bold text-slate-800">{user?.name}</p>
              <p className="text-xs text-slate-500">My account</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-300" />
          </CardContent>
        </Card>

        {/* Dependent accounts */}
        {dependents.map(dep => (
          <Card key={dep.user_id}
            className="cursor-pointer hover:shadow-md transition-shadow border-2 border-purple-200 hover:border-purple-400 bg-purple-50/30"
            onClick={() => handleSelectDependent(dep)} data-testid={`select-dependent-${dep.user_id}`}>
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-heading font-bold text-slate-800">{dep.name}</p>
                  <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">Minor</Badge>
                </div>
                <p className="text-xs text-slate-500">{dep.email} · {dep.relationship}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-purple-300" />
            </CardContent>
          </Card>
        ))}

        <p className="text-xs text-center text-slate-400 mt-4">
          Guardian access requires your password for your child's security.
        </p>
      </div>

      {/* Verification dialog */}
      <Dialog open={showVerify} onOpenChange={setShowVerify}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-500" />Guardian Verification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Please re-enter your password to access <strong>{selectedDependent?.name}</strong>'s account.
            </p>
            <Input
              type="password"
              value={verifyPassword}
              onChange={(e) => setVerifyPassword(e.target.value)}
              placeholder="Your password"
              autoFocus
              data-testid="picker-verify-password"
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyAndSwitch()}
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => { setShowVerify(false); setVerifyPassword(''); }}>Cancel</Button>
            <Button
              className="rounded-full bg-purple-600 hover:bg-purple-700"
              disabled={verifying || !verifyPassword}
              onClick={handleVerifyAndSwitch}
              data-testid="picker-verify-btn"
            >
              {verifying ? 'Verifying...' : 'Verify & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountPicker;
